import React, { createContext, useState, useEffect, useCallback } from 'react';
import type { AuthContextUser } from 'contexts';
import type { ResultType as TeamState } from 'api/TeamController/get';
import type { AgentsContextUser } from 'contexts/AgentsContext';
import { get as getTeamById, getTeamDefaultContact } from 'api/TeamController';
import { BuildJsonType } from '@bb/domain/dist/types';
import { featureEnabled } from 'utils';
import {
  TabKey,
  TeamDefaultContactType,
} from '../Router/pages/TeamSettingsPage/types';

export type { TeamState };

export interface TeamContextType {
  activeTab: TabKey;
  setActiveTab: React.Dispatch<React.SetStateAction<TabKey>>;
  /*
    Fetched team state on page load used for the team name in the header
    Details are not guaranteed to be up to date
  */
  availableTeamStates: TeamState[];
  targetTeamId: string;
  setTargetTeamId: React.Dispatch<React.SetStateAction<string>>;
  targetTeamState: TeamState;
  setTargetTeamState: React.Dispatch<React.SetStateAction<TeamState>>;
  loading: boolean;
  saving: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  teamMemberList: BuildJsonType<AgentsContextUser>[];
  contacts: TeamDefaultContactType[];
  setContacts: React.Dispatch<React.SetStateAction<TeamDefaultContactType[]>>;
  fetchTeam: () => Promise<void>;
  selectedUsers: string[];
  setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>;
}

const TeamContext: React.Context<TeamContextType> = createContext({} as any);

export const TeamContextProvider: React.FC<{
  auth: AuthContextUser;
}> = ({ auth, children }) => {
  const [availableTeamStates, setAvailableTeamStates] = useState<TeamState[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [targetTeamId, setTargetTeamId] = useState<string>(
    auth?.teams[0]?.toString(),
  );
  const [targetTeamState, setTargetTeamState] = useState<TeamState>({
    id: '',
    _id: '',
    name: '',
    createdAt: '',
    updatedAt: '',
    orgId: '',
    users: [],
    email: '',
    sendNotificationToTeamEmail: false,
  });

  const [activeTab, setActiveTab] = useState<TabKey>('DETAILS');

  const fetchTeams = useCallback(async () => {
    const dataArray = auth.teams
      ? await Promise.all(auth.teams.map(id => getTeamById(id.toString())))
      : [];

    setAvailableTeamStates(dataArray);
  }, [auth]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // When auth.teams changes (such as via teams_updated Pusher event),
  // ensure targetTeamId (the team tab selected) is valid and points to an existing team
  useEffect(() => {
    if (!featureEnabled('teamChangePusherUpdateFeature', 1)) {
      return;
    }

    if (!auth?.teams || auth.teams.length === 0) {
      setTargetTeamId('');
      return;
    }

    const teamsArray = auth.teams.map(t => t.toString());
    const currentTargetAsString = targetTeamId?.toString();

    // If current targetTeamId is no longer in the teams list, switch to the first team
    if (currentTargetAsString && !teamsArray.includes(currentTargetAsString)) {
      setTargetTeamId(teamsArray[0]);
    } else if (!targetTeamId && teamsArray.length > 0) {
      // If targetTeamId is undefined and we have teams, set it to the first team
      setTargetTeamId(teamsArray[0]);
    }
  }, [auth?.teams, targetTeamId]);

  const fetchTeam = useCallback(async () => {
    if (!targetTeamId) return;

    setLoading(true);

    const data = await getTeamById(targetTeamId);
    setTargetTeamState(data);

    const clonedAvailableTeamStates = [...availableTeamStates];
    const index = clonedAvailableTeamStates.findIndex(
      team => team.id === targetTeamId,
    );

    if (index !== -1) {
      clonedAvailableTeamStates[index] = data;
      setAvailableTeamStates(clonedAvailableTeamStates);
    }

    setLoading(false);
  }, [targetTeamId, setTargetTeamState, setLoading]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // Creating member list for UserSelect props
  const teamMemberList = React.useMemo(
    () =>
      targetTeamState.users.map(user => ({
        addressName: auth.org.locations[+user.location].name,
        avatarFilestackId: user.avatarFilestackId,
        firstName: user.firstName,
        id: user.id,
        label: `${user.firstName} ${user.lastName}`,
        licenseType: user.licenseType,
        lastName: user.lastName,
        location: user.location,
        role: user.role,
        mls_numbers: user.mls_numbers,
      })),
    [targetTeamState],
  );

  const [contacts, setContacts] = useState<TeamDefaultContactType[]>([]);

  const [selectedUsers, setSelectedUsers] = React.useState([]);

  useEffect(() => {
    if (!targetTeamState.users.length || !targetTeamId) return;

    const fetchContacts = async () => {
      const contactList = await getTeamDefaultContact(targetTeamId);
      setContacts(
        contactList
          .map(contact => {
            const teamContact = teamMemberList?.find(
              member => member.id === contact.contactId,
            );

            if (!teamContact) {
              // Filters out users from other branches for BA
              return null!;
            }

            return {
              ...contact,
              ...teamContact,
            };
          })
          .filter(Boolean),
      );
    };
    fetchContacts();
  }, [targetTeamId, targetTeamState.users, teamMemberList]);

  return (
    <TeamContext.Provider
      value={{
        activeTab,
        setActiveTab,
        targetTeamState,
        setTargetTeamState,
        availableTeamStates,
        targetTeamId,
        setTargetTeamId,
        loading,
        saving,
        setSaving,
        contacts,
        teamMemberList,
        setContacts,
        fetchTeam,
        selectedUsers,
        setSelectedUsers,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export default TeamContext;
